import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { CrmAccountListItem, CrmAccountStatus } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import { formatMoney } from '@/utils/currency';
import type { AppShellContext } from '@/components/layout/AppShell';
import { AccountFormDialog } from './AccountFormDialog';

const PAGE_SIZE = 20;
const COLUMN_COUNT = 8;
const ALL_STATUSES = 'all';

const STATUS_STYLES: Record<CrmAccountStatus, string> = {
  lead: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  prospect: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  customer: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  inactive: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

export function AccountList() {
  const { nip } = useOutletContext<AppShellContext>();
  const { listAccounts } = useCrmApi();
  const navigate = useNavigate();

  const [items, setItems] = useState<CrmAccountListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(ALL_STATUSES);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listAccounts(nip, {
        search: search.trim() || undefined,
        status: status === ALL_STATUSES ? undefined : (status as CrmAccountStatus),
        page,
        pageSize: PAGE_SIZE,
      });
      if (seq !== loadSeq.current) return;
      setItems(result.items);
      setTotal(result.total);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) setForbidden(true);
      else toast.error(t('errors.loadingFailed'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, search, status, page, listAccounts]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (forbidden) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <p className="text-muted-foreground text-sm">{t('errors.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setPage(1);
            setSearch(event.target.value);
          }}
          placeholder={t('crm.accounts.searchPlaceholder')}
          className="max-w-sm"
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('crm.accounts.allStatuses')}</SelectItem>
            {(['lead', 'prospect', 'customer', 'inactive'] as CrmAccountStatus[]).map((value) => (
              <SelectItem key={value} value={value}>
                {t(`crm.accountStatus.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('crm.accounts.newAccount')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('crm.accounts.columns.name')}</TableHead>
                <TableHead>{t('crm.accounts.columns.nip')}</TableHead>
                <TableHead>{t('crm.accounts.columns.status')}</TableHead>
                <TableHead>{t('crm.accounts.columns.city')}</TableHead>
                <TableHead>{t('crm.accounts.columns.owner')}</TableHead>
                <TableHead className="text-right">{t('crm.accounts.columns.openDeals')}</TableHead>
                <TableHead className="text-right">{t('crm.accounts.columns.openValue')}</TableHead>
                <TableHead>{t('crm.accounts.columns.lastActivity')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground py-10 text-center">
                    {t('crm.accounts.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((account) => (
                  <TableRow
                    key={account.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/${nip}/crm/klienci/${account.id}`)}
                  >
                    <TableCell className="font-medium">{account.name}</TableCell>
                    <TableCell className="text-sm">{account.nip ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={STATUS_STYLES[account.status]}>
                        {t(`crm.accountStatus.${account.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{account.city ?? '—'}</TableCell>
                    <TableCell className="text-sm">{account.ownerDisplayName ?? '—'}</TableCell>
                    <TableCell className="text-right text-sm">{account.openDeals}</TableCell>
                    <TableCell className="text-right text-sm">
                      {account.openValueNet === null ? '—' : formatMoney(account.openValueNet, 'PLN', 2)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {account.lastActivityAt
                        ? dayjs(account.lastActivityAt).format('DD.MM.YYYY')
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-muted-foreground px-2 text-sm">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <AccountFormDialog
        nip={nip}
        open={formOpen}
        account={null}
        onOpenChange={setFormOpen}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
    </div>
  );
}
